import { HUText } from "@modules/ui/components/huText"
import { RichTextMarkdownPaste } from "@modules/ui/components/richText/richTextMardownPaste"
import { RichTextMenuBar } from "@modules/ui/components/richText/richTextMenuBar"
import Link from "@tiptap/extension-link"
import Underline from "@tiptap/extension-underline"
import { EditorContent, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { useTheme } from "styled-components"

type HURichTextInputProps = {
  label?: string
  isRequiredInput?: boolean
  value: string
  onChange: (value: string) => void
  maxLength: number
  isInvalid: boolean
  error?: React.JSX.Element
  paragrapheFontStyle: "BL" | "BM" | "BS" | "BXS"
  placeholder?: string
}

export const HURichTextInput = ({
  label,
  value = "",
  isRequiredInput,
  onChange,
  maxLength,
  paragrapheFontStyle,
  isInvalid,
  error,
  placeholder,
}: HURichTextInputProps) => {
  const theme = useTheme()
  const editor = useEditor({
    enableInputRules: false,
    extensions: [
      StarterKit,
      Underline,
      RichTextMarkdownPaste,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
        protocols: ["http", "https"],
        isAllowedUri: (url, ctx) => {
          try {
            // construct URL
            const parsedUrl = url.includes(":") ? new URL(url) : new URL(`${ctx.defaultProtocol}://${url}`)

            // use default validation
            if (!ctx.defaultValidate(parsedUrl.href)) {
              return false
            }

            // disallowed protocols
            const disallowedProtocols = ["ftp", "file", "mailto"]
            const protocol = parsedUrl.protocol.replace(":", "")

            if (disallowedProtocols.includes(protocol)) {
              return false
            }

            // only allow protocols specified in ctx.protocols
            const allowedProtocols = ctx.protocols.map((p) => (typeof p === "string" ? p : p.scheme))

            if (!allowedProtocols.includes(protocol)) {
              return false
            }

            // all checks have passed
            return true
          } catch {
            return false
          }
        },
        shouldAutoLink: (url) => {
          try {
            // construct URL
            const parsedUrl = url.includes(":") ? new URL(url) : new URL(`https://${url}`)

            // only auto-link if the domain is not in the disallowed list
            const disallowedDomains = [""]
            const domain = parsedUrl.hostname

            return !disallowedDomains.includes(domain)
          } catch {
            return false
          }
        },
      }),
    ],
    content: value,
    onBlur({ editor }) {
      // The content has changed.
      onChange(editor.getHTML())
    },
  })

  return (
    <div className="w-full">
      <HUText fontStyle="LS" className="uppercase flex-1">
        {label}
        {isRequiredInput && (
          <HUText fontStyle="LL" color={theme.necessaryInput}>
            {" "}
            *
          </HUText>
        )}
      </HUText>

      <RichTextMenuBar editor={editor} />
      <EditorContent
        editor={editor}
        className={
          "hu-richtext-input " +
          paragrapheFontStyle +
          (isInvalid ? " error " : " ") +
          (editor?.isFocused ? " focused " : "")
        }
        maxLength={maxLength}
        placeholder={placeholder}
        onClick={() => {
          editor?.commands.focus()
        }}
      />
      {error}
    </div>
  )
}
